import React, { useState, useMemo } from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getJerarquiaNombre } from '../../../../const/jerarquia';

const useStyles = makeStyles()((theme) => ({
    root: {
        width: '100%',
    },
    areaGroup: {
        marginLeft: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    areaHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.5, 0),
    },
    areaTitle: {
        flex: 1,
        fontWeight: 500,
    },
    tableroItem: {
        marginLeft: theme.spacing(4),
        padding: theme.spacing(0.5, 0),
    },
    jerarquiaLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(1),
    },
    selectAllButton: {
        marginLeft: theme.spacing(1),
        fontSize: '0.75rem',
        textTransform: 'none',
        minWidth: 'auto',
        padding: theme.spacing(0.5, 1),
    },
}));

/**
 * Construye un árbol jerárquico de áreas con sus tableros
 * Usa la estructura de areasTree para mantener la jerarquía correcta
 * @param {Array} items - Array de tableros con información de área
 * @param {Array} areasTree - Árbol de áreas completo (para construir jerarquía)
 * @returns {Array} - Árbol estructurado como array de nodos
 */
const buildAreaTree = (items, areasTree) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
        return [];
    }

    // Crear un mapa de áreas con sus tableros
    const areaMap = {};
    
    items.forEach(tablero => {
        if (!tablero || !tablero.area) {
            return;
        }
        
        const areaId = tablero.area.id_area;
        if (!areaMap[areaId]) {
            areaMap[areaId] = {
                id_area: areaId,
                nombre: tablero.area.nombre,
                jerarquia: tablero.area.jerarquia,
                tableros: [],
            };
        }
        areaMap[areaId].tableros.push({
            id_tablero: tablero.id_tablero,
            nombre: tablero.area.nombre || `Tablero ${tablero.id_tablero}`,
        });
    });

    // Si no tenemos el árbol de áreas, devolver estructura plana
    if (!areasTree || !Array.isArray(areasTree) || areasTree.length === 0) {
        const flatAreas = Object.values(areaMap).sort((a, b) => {
            if (a.jerarquia !== b.jerarquia) {
                return a.jerarquia - b.jerarquia;
            }
            return a.nombre.localeCompare(b.nombre);
        });
        return flatAreas;
    }

    // Construir árbol usando la estructura de areasTree (que ya tiene subareas)
    const buildTree = (areas) => {
        if (!areas || !Array.isArray(areas)) return [];

        return areas
            .map(area => {
                // Obtener datos del área (con tableros si tiene)
                const areaData = areaMap[area.id_area] || {
                    id_area: area.id_area,
                    nombre: area.nombre,
                    jerarquia: area.jerarquia,
                    tableros: [],
                };

                // Construir subáreas recursivamente
                const subareas = area.subareas && Array.isArray(area.subareas) && area.subareas.length > 0
                    ? buildTree(area.subareas)
                    : [];

                // Solo incluir el área si tiene tableros o tiene subáreas con tableros
                const hasTableros = areaData.tableros && areaData.tableros.length > 0;
                const hasSubareasWithTableros = subareas.length > 0;

                if (hasTableros || hasSubareasWithTableros) {
                    return {
                        ...areaData,
                        subareas: subareas,
                    };
                }

                return null;
            })
            .filter(area => area !== null); // Filtrar áreas sin tableros ni subáreas con tableros
    };

    return buildTree(areasTree);
};

/**
 * Obtiene los IDs de tableros SOLO de esta área (no incluye hijos)
 */
const getAreaTableroIds = (node) => {
    if (!node.tableros || !Array.isArray(node.tableros)) return [];
    return node.tableros.map(t => t.id_tablero);
};

/**
 * Obtiene todos los IDs de tableros de esta rama completa (área + todas las subáreas)
 */
const getAllBranchTableroIds = (node) => {
    const ids = [];
    // Agregar tableros de esta área
    if (node.tableros && Array.isArray(node.tableros)) {
        ids.push(...node.tableros.map(t => t.id_tablero));
    }
    // Agregar tableros de todas las subáreas recursivamente
    if (node.subareas && Array.isArray(node.subareas)) {
        node.subareas.forEach(subarea => {
            ids.push(...getAllBranchTableroIds(subarea));
        });
    }
    return ids;
};

/**
 * Verifica si todos los tableros de esta área específica están seleccionados (sin incluir hijos)
 */
const areAllAreaTablerosSelected = (node, selectedIds) => {
    const areaIds = getAreaTableroIds(node);
    if (areaIds.length === 0) return false;
    return areaIds.every(id => selectedIds.includes(id));
};

/**
 * Verifica si algunos (pero no todos) tableros de esta área específica están seleccionados
 */
const areSomeAreaTablerosSelected = (node, selectedIds) => {
    const areaIds = getAreaTableroIds(node);
    if (areaIds.length === 0) return false;
    const selectedCount = areaIds.filter(id => selectedIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < areaIds.length;
};

/**
 * Verifica si todos los tableros de esta rama completa están seleccionados
 */
const areAllBranchTablerosSelected = (node, selectedIds) => {
    const allIds = getAllBranchTableroIds(node);
    if (allIds.length === 0) return false;
    return allIds.every(id => selectedIds.includes(id));
};

/**
 * Verifica si algunos (pero no todos) tableros de esta rama completa están seleccionados
 */
const areSomeBranchTablerosSelected = (node, selectedIds) => {
    const allIds = getAllBranchTableroIds(node);
    if (allIds.length === 0) return false;
    const selectedCount = allIds.filter(id => selectedIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < allIds.length;
};

export function CheckBoxTree({ 
    value = [], 
    onChange, 
    onBlur, 
    items = [], 
    areasTree = [],
    label,
    error,
    helperText,
    disabled = false 
}) {
    const { classes } = useStyles();
    const [expandedAreas, setExpandedAreas] = useState({});

    // Construir el árbol de áreas con tableros
    const tree = useMemo(() => {
        return buildAreaTree(items, areasTree);
    }, [items, areasTree]);

    // Convertir value a array si es necesario
    const selectedIds = useMemo(() => {
        if (Array.isArray(value)) return value;
        if (value) return [value];
        return [];
    }, [value]);

    const handleTableroChange = (tableroId, checked) => {
        let newSelectedIds;
        if (checked) {
            newSelectedIds = [...selectedIds, tableroId];
        } else {
            newSelectedIds = selectedIds.filter(id => id !== tableroId);
        }
        onChange(newSelectedIds);
    };

    // Seleccionar todos los tableros de esta rama completa (área + todas las subáreas)
    const handleAreaSelectAll = (node, checked) => {
        const allBranchIds = getAllBranchTableroIds(node);
        let newSelectedIds = [...selectedIds];

        if (checked) {
            // Agregar todos los IDs de la rama que no estén ya seleccionados
            allBranchIds.forEach(id => {
                if (!newSelectedIds.includes(id)) {
                    newSelectedIds.push(id);
                }
            });
        } else {
            // Remover todos los IDs de la rama
            newSelectedIds = newSelectedIds.filter(id => !allBranchIds.includes(id));
        }

        onChange(newSelectedIds);
    };

    const toggleAreaExpanded = (areaId) => {
        setExpandedAreas(prev => ({
            ...prev,
            [areaId]: !prev[areaId],
        }));
    };

    const renderNode = (node, level = 0) => {
        const isExpanded = expandedAreas[node.id_area] !== false; // Por defecto expandido
        const areaTableros = node.tableros || [];
        const hasTableros = areaTableros.length > 0;
        const hasSubareas = node.subareas && Array.isArray(node.subareas) && node.subareas.length > 0;
        
        // Para el checkbox del área, solo considerar los tableros de esta área específica
        const allAreaSelected = hasTableros ? areAllAreaTablerosSelected(node, selectedIds) : false;
        const someAreaSelected = hasTableros ? areSomeAreaTablerosSelected(node, selectedIds) : false;
        
        // Para "Seleccionar todos", considerar toda la rama (área + subáreas)
        const allBranchIds = getAllBranchTableroIds(node);
        const allBranchSelected = allBranchIds.length > 0 ? areAllBranchTablerosSelected(node, selectedIds) : false;
        const someBranchSelected = allBranchIds.length > 0 ? areSomeBranchTablerosSelected(node, selectedIds) : false;

        return (
            <Box key={node.id_area} className={classes.areaGroup} style={{ marginLeft: `${level * 24}px` }}>
                <Box className={classes.areaHeader}>
                    <Box style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        {hasTableros && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={allAreaSelected}
                                        indeterminate={someAreaSelected && !allAreaSelected}
                                        onChange={(e) => {
                                            // El checkbox del área solo afecta a los tableros de esta área
                                            const areaIds = getAreaTableroIds(node);
                                            let newSelectedIds = [...selectedIds];
                                            if (e.target.checked) {
                                                areaIds.forEach(id => {
                                                    if (!newSelectedIds.includes(id)) {
                                                        newSelectedIds.push(id);
                                                    }
                                                });
                                            } else {
                                                newSelectedIds = newSelectedIds.filter(id => !areaIds.includes(id));
                                            }
                                            onChange(newSelectedIds);
                                        }}
                                        disabled={disabled}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography className={classes.areaTitle}>
                                            {node.nombre}
                                        </Typography>
                                        <Typography className={classes.jerarquiaLabel}>
                                            {getJerarquiaNombre(node.jerarquia)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        )}
                        {!hasTableros && (
                            <Box>
                                <Typography className={classes.areaTitle}>
                                    {node.nombre}
                                </Typography>
                                <Typography className={classes.jerarquiaLabel}>
                                    {getJerarquiaNombre(node.jerarquia)}
                                </Typography>
                            </Box>
                        )}
                        {(hasTableros || hasSubareas) && allBranchIds.length > 0 && (
                            <Button
                                size="small"
                                className={classes.selectAllButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAreaSelectAll(node, !allBranchSelected);
                                }}
                                disabled={disabled}
                            >
                                {allBranchSelected ? 'Deseleccionar todos' : 'Seleccionar todos'}
                            </Button>
                        )}
                    </Box>
                    {hasSubareas && (
                        <IconButton
                            size="small"
                            onClick={() => toggleAreaExpanded(node.id_area)}
                            disabled={disabled}
                        >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    )}
                </Box>

                <Collapse in={isExpanded}>
                    {/* Subáreas (renderizadas recursivamente) */}
                    {hasSubareas && (
                        <Box>
                            {node.subareas.map(subarea => renderNode(subarea, level + 1))}
                        </Box>
                    )}
                </Collapse>
            </Box>
        );
    };

    return (
        <FormControl 
            variant="standard" 
            className={classes.root}
            error={error}
            disabled={disabled}
        >
            {label && (
                <Typography variant="caption" component="legend" color="textSecondary" style={{ marginBottom: 8 }}>
                    {label}
                </Typography>
            )}
            <Box>
                {Array.isArray(tree) && tree.length > 0 ? (
                    tree.map(node => renderNode(node, 0))
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        {items && items.length > 0 
                            ? "No hay ítems disponibles para las áreas seleccionadas" 
                            : "No hay ítems disponibles"}
                    </Typography>
                )}
            </Box>
            {helperText && (
                <Typography variant="caption" color={error ? 'error' : 'textSecondary'} style={{ marginTop: 4 }}>
                    {helperText}
                </Typography>
            )}
        </FormControl>
    );
}
